/**
 * エントリポイント。
 *
 * ゲーム本体（Phaser）と DOM UI を組み立てる。
 * ステージ（都道府県 × モード）のコンテンツはここで差し替えるだけで切り替わる。
 */

import './styles/main.css';
import './styles/extras.css';

import { EventBus } from './core/EventBus';
import { GameState, GameStateMachine } from './core/GameState';
import { createGame } from './game/createGame';
import {
  APP_CONFIG,
  LEGACY_STORAGE,
  metaStorageKey,
  settingsStorageKey,
  stageStorageKey,
} from './game/config/gameConfig';
import {
  STAGE_IDS,
  getStageOrFallback,
  isRootPath,
  resolveStageIdFromPath,
  stageIdOf,
} from './game/config/stages';
import { resolveAudioPaths } from './game/config/audio';
import { idMigrationFor } from './game/config/idMigrations';
import { validateStage } from './game/config/validate';
import { buildItemIndex, type GameContext } from './game/GameContext';
import { AudioSystem } from './game/systems/AudioSystem';
import { StorageSystem } from './game/systems/StorageSystem';
import { IconProvider, probeItemImages, probeSceneryPhotos, probeSceneryTour } from './game/utils/assetLoader';
import { EvolutionList } from './ui/EvolutionList';
import { HudController } from './ui/HudController';
import { KeyboardController } from './ui/KeyboardController';
import { renderLandingScreen } from './ui/LandingScreen';
import { OverlayController } from './ui/OverlayController';
import { PwaInstallPrompt } from './ui/PwaInstallPrompt';
import { applyStageTheme, lockTouchScroll, watchLayout, watchScenePhase } from './ui/responsive';
import { RewardModal } from './ui/RewardModal';
import { SettingsModal } from './ui/SettingsModal';
import { StageSelectModal } from './ui/StageSelectModal';
import { TipsCardModal } from './ui/TipsCardModal';
import { registerServiceWorker } from './pwa';
import { AssistController } from './meta/assist';
import { DailyBook } from './meta/daily';
import { RankingIdentityStore } from './meta/dailyRanking';
import { todaysDaily } from './meta/dailyStage';
import { AppPrompts } from './meta/appPrompts';
import { confirmDialog } from './ui/confirmDialog';
import { RankingModal } from './ui/RankingModal';
import { TrophyBook } from './meta/progress';
import { Wallet } from './meta/wallet';
import { detectPlatform } from './platform/detect';
import { onAdCovering, onAdUnavailable } from './platform/adLifecycle';
import { isNativeApp, PUBLIC_ORIGIN } from './platform/native';
import { AssistBar } from './ui/AssistBar';
import { HighlightRecorder, canRecordHighlights } from './ui/HighlightRecorder';
import { MomentCapture } from './ui/MomentCapture';
import { ShopModal } from './ui/ShopModal';
import { DishCard } from './ui/DishCard';
import { EvolutionStrip } from './ui/EvolutionStrip';
import type { StageItemDefinition } from './game/config/types';
import { applyInputMode, applyTextSizeFromStorage } from './ui/inputMode';
import { installModalFocus } from './ui/modalFocus';
import { startupNotices } from './ui/noticeQueue';
import { TourChip } from './ui/TourChip';
import { TrophyShelf, renderTrophyBadge } from './ui/TrophyShelf';
import { StageLoader } from './ui/StageLoader';
import { startTelemetry } from './meta/telemetrySetup';
import { attachTelemetryHooks } from './meta/telemetryHooks';
import { getTelemetry, track, trackEarly } from './meta/telemetry';
import { ALL_PREFECTURE_IDS } from './game/config/regions';
import { collectStageSummaries } from './ui/StageSelectModal';
import { applyStreamerMode, isStreamerMode } from './ui/streamerMode';

async function bootstrap(): Promise<void> {
  const appRoot = document.getElementById('app');
  const gameRoot = document.getElementById('game-root');
  if (!appRoot || !gameRoot) {
    console.error('[main] 必要な DOM 要素が見つかりません。');
    return;
  }

  /* ---- トップは県えらび。ゲーム本体は起動しない ----
   * 47県ある以上、`/` でいきなり1県が立ち上がると「その県のゲーム」に見えてしまう。
   * ここで返すので、Phaser も県の画像も読み込まない。 */
  // スマホではキー操作の説明を隠す（<html data-input="touch">）
  applyInputMode();
  applyTextSizeFromStorage(settingsStorageKey());
  installModalFocus();

  // トロフィーは Web 版でもアプリ版でも出す（Web 版の機能を落とさない。D7 の計測にも効く）
  const trophies = new TrophyBook();

  // 今日の県のお知らせ・レビュー依頼（iOS アプリだけ動く。Web・モック・Android では何もしない）
  const appPrompts = new AppPrompts(detectPlatform());
  if (appPrompts.canNotify) {
    // 起動のたびに数日分を予約し直す（入れている人だけ）。タップで開いたら今日の県へ
    void appPrompts.refreshNotices().catch(() => undefined);
    detectPlatform().onNoticeOpened((href) => {
      // 計測がまだ始まっていなくても落とさない（始まったときに入れる）
      trackEarly('notice_open');
      if (`${location.pathname}${location.search}` !== href) {
        // 移る前に端末へ書いておく（計測を始めると、覚えておいた分が保存される）
        if (!getTelemetry()) startTelemetry();
        location.assign(href);
      }
    });
  }

  if (isRootPath(location.pathname)) {
    // 遊び方の記録（設定で切っていれば何もしない）
    startTelemetry();
    renderLandingScreen(appRoot, { trophies, platform: detectPlatform() });
    return;
  }

  // 盤面の「仕込み中」の幕（HTML に書いてあり、すでに見えている）
  const loader = new StageLoader(document);

  /* ---- ステージ（コンテンツ）の読み込みと検証 ---- */
  // URL のパスからステージを決める（例: /hokkaido → hokkaido-food）
  const requestedStageId = resolveStageIdFromPath(
    location.pathname,
    STAGE_IDS,
    APP_CONFIG.defaultStageId,
  );
  const stage = getStageOrFallback(requestedStageId);
  const validation = validateStage(stage);
  validation.errors.forEach((message) => console.error(`[stage] ${message}`));
  validation.warnings.forEach((message) => console.warn(`[stage] ${message}`));

  const items = [...stage.items].sort((a, b) => a.level - b.level);

  // 県えらびの「つづきから」に使う（下書きの県は記録しない）
  if (!stage.draft) {
    try {
      localStorage.setItem(metaStorageKey('last-stage'), stageIdOf(stage));
    } catch {
      // 保存できなくても遊べる
    }
  }

  /* ---- 基盤システム ---- */
  const bus = new EventBus();
  const state = new GameStateMachine(GameState.LOADING);
  // 記録はステージ別、設定はステージ共通。旧キーからの移行つき。
  const stageId = stageIdOf(stage);
  const storage = new StorageSystem({
    key: stageStorageKey(stageId),
    settingsKey: settingsStorageKey(),
    legacyKey: stageId === LEGACY_STORAGE.stageId ? LEGACY_STORAGE.save : undefined,
    idMigration: idMigrationFor(stageId),
  });
  const settings = storage.settings;
  applyStreamerMode(settings.streamerMode);

  // Lv.1 は最初から遊べる料理なので、図鑑を最初から解放しておく
  const level1Item = items.find((item) => item.level === 1);
  if (level1Item) {
    storage.unlockItem(level1Item.id);
  }

  applyStageTheme(stage.theme);
  watchLayout(appRoot);
  watchScenePhase(bus);
  lockTouchScroll(gameRoot);

  /* ---- 画像プローブ（存在するものだけを本番画像として使う） ----
   * 料理・時間帯写真・背景ツアーの3種をまとめて投げる。
   * 逐次 await にすると起動前に3往復ぶん待たされ、初見ユーザーの体感に効く。 */
  const probeOptions = { enabled: APP_CONFIG.probeImages };
  const [availability, sceneryPhotos, tourPhotos] = await Promise.all([
    probeItemImages(items, probeOptions),
    probeSceneryPhotos(stage, probeOptions),
    probeSceneryTour(stage, probeOptions),
  ]);
  const icons = new IconProvider(availability);
  loader.set(0.25);

  const audio = new AudioSystem({
    paths: resolveAudioPaths(stage.audio),
    bgmVolume: settings.bgmVolume,
    seVolume: settings.seVolume,
  });
  void audio.init();

  // 今日の県：URL が ?daily=<今日の日付> で、今日の県と一致するときだけ
  const today = todaysDaily();
  const dailyParam = new URLSearchParams(location.search).get('daily');
  const daily =
    today && dailyParam === today.dateKey && today.stageId === stageIdOf(stage)
      ? { dateKey: today.dateKey, seed: today.seed, shortDate: today.shortDate }
      : undefined;
  // 前の日に共有された今日の県のリンクを開いた人へ：今日の県を案内する（このまま遊ぶこともできる）
  // 読み込みが終わって開始画面が出てから聞く（開始画面より手前に出すため）
  if (today && dailyParam && !daily) {
    const off = state.onChange((to) => {
      if (to !== GameState.READY) return;
      off();
      window.setTimeout(() => {
        void confirmDialog({
          title: 'このリンクの今日の県は終わりました',
          body: `今日の県は${today.regionName}です。このまま${stage.regionName}で遊ぶこともできます（ふつうのプレイになります）。`,
          okLabel: `今日の県（${today.regionName}）へ`,
          cancelLabel: 'このまま遊ぶ',
        }).then((go) => {
          if (go) location.assign(today.href);
        });
      }, 50);
    });
  }

  // 広告・課金・おはし。Web 版の本番では platform.monetized が false で何も出ない
  const assist = new AssistController(detectPlatform(), new Wallet());
  // 合間の広告は「前回の広告から実際に遊んだ時間」で数える（2026-10-13）。
  // ゲーム中で画面が出ているあいだだけ、5秒ごとに足す（端末に保存するので、閉じても続きから）
  const HEARTBEAT_MS = 5_000;
  window.setInterval(() => {
    if (document.visibilityState !== 'visible') return;
    assist.heartbeat(state.is(GameState.PLAYING) ? HEARTBEAT_MS : 0);
  }, HEARTBEAT_MS);
  // 配信モードのあいだは合間の広告を出さない（ごほうび広告は本人が押したときだけなので残す）
  assist.blockInterstitial = () => context.settings.streamerMode;
  const gifts = assist.claimStartupGifts();
  // アプリ版：同意の確認・広告の先読み・買い切りの確認（待たずにゲームを始める）
  void assist.startup();
  // 広告が画面を覆っているあいだはBGMを止める
  onAdCovering((covering) => (covering ? audio.pauseForAd() : audio.resumeAfterAd()));
  // ごほうび広告を押したのに出せなかったとき（新しいアプリは広告が来にくい）。固まったように見せない
  onAdUnavailable(() => showNow('いまは広告を読み込めません。少し時間をおいてから、もう一度お試しください。'));

  const context: GameContext = {
    stage,
    items,
    itemByLevel: buildItemIndex(items),
    maxLevel: items.length,
    bus,
    state,
    storage,
    audio,
    icons,
    sceneryPhotos,
    tourPhotos,
    daily,
    assist,
    settings,
    reportLoadProgress: (value) => loader.set(0.25 + value * 0.75),
  };

  // 遊び方の記録。ゲーム本体より先につなぐ（リスタートで点が 0 に戻る前の値を控えるため）
  const telemetry = startTelemetry({
    enabled: () => context.settings.sendUsageStats,
    region: ALL_PREFECTURE_IDS.includes(stage.regionId) ? stage.regionId : undefined,
  });
  attachTelemetryHooks(context, telemetry, {
    totalPlaysBefore: () => collectStageSummaries().reduce((sum, s) => sum + s.playCount, 0),
  });

  state.onChange((to, from) => bus.emit('state:change', { from, to }));
  state.onChange((to) => {
    if (to !== GameState.LOADING) loader.finish();
  });

  /* ---- DOM UI ---- */
  new HudController(context, appRoot);
  const evolutionList = new EvolutionList(context, document);
  new SettingsModal(context, document);
  bus.on('settings:change', ({ settings: next }) => applyStreamerMode(next.streamerMode));
  const shop = new ShopModal(assist);

  // トロフィー棚（ヘッダーの🏆とゲームオーバー画面から開く）
  const trophyShelf = new TrophyShelf(trophies);
  const trophyButton = document.querySelector<HTMLButtonElement>('[data-ui="trophy-open"]');
  const refreshTrophyBadge = () => {
    if (trophyButton) renderTrophyBadge(trophyButton, trophies.unseenCount);
  };
  trophies.sync();
  refreshTrophyBadge();
  const openTrophies = () => {
    if (context.state.is(GameState.PLAYING)) context.bus.emit('command:pause');
    trophyShelf.open();
    refreshTrophyBadge();
  };
  trophyButton?.addEventListener('click', openTrophies);

  let recorder: HighlightRecorder | null = null;
  let moments: MomentCapture | null = null;
  new OverlayController(context, document, {
    getHighlight: () => recorder?.clip ?? null,
    getMoments: () => moments?.list() ?? [],
    trophies,
    appPrompts,
    onOpenTrophies: openTrophies,
    onTrophiesSynced: refreshTrophyBadge,
    onOpenShop: () => shop.open('result'),
    daily: daily ? new DailyBook() : undefined,
    ranking: daily
      ? (() => {
          const ids = new RankingIdentityStore();
          return { ids, modal: new RankingModal(ids) };
        })()
      : undefined,
  });

  // おはし・ショップ（アプリ版と staging のモックだけ）
  new AssistBar(context, shop, document);
  new TourChip(context, document);

  // のんびりモード中の目印（盤面の下）
  const modeChip = document.createElement('div');
  modeChip.className = 'mode-chip';
  modeChip.textContent = '🍵 のんびり（こぼれない・記録なし）';
  modeChip.hidden = true;
  document.querySelector('.stage-frame')?.append(modeChip);
  bus.on('mode:change', ({ relaxed }) => {
    modeChip.hidden = !relaxed;
  });
  if (assist.enabled) {
    const shopButton = document.createElement('button');
    shopButton.type = 'button';
    shopButton.className = 'btn';
    shopButton.textContent = '🥢 ショップ（おはし・買い切り）';
    shopButton.addEventListener('click', () => shop.open('settings'));
    document.querySelector('[data-ui="settings-modal"] .modal__body')?.append(shopButton);
  }
  if (appPrompts.canNotify) mountDailyNoticeToggle(appPrompts);
  if (gifts.welcome > 0 || gifts.daily) {
    // 起動直後に出すとルール説明と重なり、上のボタンも隠れる。
    // 「はじめる」を押して遊び始めたときに1回だけ出す（2026-10-04）
    const message =
      gifts.welcome > 0
        ? `ようこそ。おはしを${gifts.welcome}本どうぞ（料理を1つつまんで消せます）`
        : '買い切り特典：今日のおはしが1本届きました';
    const off = bus.on('state:change', ({ to }) => {
      if (to !== GameState.PLAYING) return;
      off();
      showToast(message, document.querySelector<HTMLElement>('.stage-frame'));
    });
  }
  new KeyboardController(context);

  // PWA インストール促進バー（Web版だけ。アプリの中では出さない）
  if (!isNativeApp()) new PwaInstallPrompt().mount(document.body);

  // 進化一覧のタイルから「食べに行く」モーダルを開ける
  const rewardModal = new RewardModal(context, document);
  const tipsModal = new TipsCardModal(context, document);

  // 地域選択（全国マップの土台）
  const telemetryRegion = ALL_PREFECTURE_IDS.includes(stage.regionId) ? stage.regionId : undefined;
  const stageSelect = new StageSelectModal(stageId, document);
  document.querySelector<HTMLButtonElement>('[data-ui="stage-open"]')?.addEventListener(
    'click',
    () => {
      if (context.state.is(GameState.PLAYING)) context.bus.emit('command:pause');
      stageSelect.open();
    },
  );
  // 図鑑のタイル・NEXT をタップ → 料理の説明カード（出会っていれば名前と説明、食べに行く）
  const dishCard = new DishCard(context);
  dishCard.onOpenReward = (item) => {
    track('eat_out', { region: telemetryRegion });
    rewardModal.open(item);
  };
  const openDish = (item: StageItemDefinition) => {
    const playing = context.state.is(GameState.PLAYING);
    if (playing) context.bus.emit('command:pause');
    dishCard.open(item, playing);
  };
  evolutionList.setRewardOpenHandler(openDish);
  // 盤面のすぐ下の進化の列（スマホ幅だけ表示）
  const strip = new EvolutionStrip(context, document.querySelector('.stage-frame'));
  strip.onOpen = openDish;
  let queue: { current: StageItemDefinition; next: StageItemDefinition } | null = null;
  bus.on('queue:change', (payload) => {
    queue = payload;
  });
  document.querySelectorAll<HTMLElement>('.next-card').forEach((card, index) => {
    card.setAttribute('role', 'button');
    card.tabIndex = 0;
    card.style.cursor = 'pointer';
    card.addEventListener('click', () => {
      if (queue) openDish(index === 0 ? queue.current : queue.next);
    });
  });
  evolutionList.setTipOpenHandler((hotspot) => {
    const resumeOnClose = context.state.is(GameState.PLAYING);
    if (resumeOnClose) {
      context.bus.emit('command:pause');
    }
    // 図鑑からの再表示なので、ここでは発見扱いにしない
    track('tip_open', { region: telemetryRegion });
    tipsModal.open({ hotspot, resumeOnClose, markAsDiscovered: false });
  });

  // モーダルを閉じたらゲームを再開する（両方閉じている場合のみ）
  const resumeIfAllClosed = () => {
    if (
      !rewardModal.isOpen() &&
      !tipsModal.isOpen() &&
      !dishCard.isOpen() &&
      context.state.is(GameState.PAUSED)
    ) {
      context.bus.emit('command:resume');
    }
  };
  rewardModal.onClose = resumeIfAllClosed;
  dishCard.onClose = (resumeOnClose) => {
    if (resumeOnClose) resumeIfAllClosed();
  };
  tipsModal.onClose = (resumeOnClose) => {
    if (resumeOnClose) resumeIfAllClosed();
  };

  // ゲーム中にホットスポットマーカーをタップしたら一時停止して豆知識カードを開く
  bus.on('tour:openTip', (payload) => {
    track('tip_open', { region: telemetryRegion });
    tipsModal.open(payload);
  });

  /* ---- Phaser ---- */
  const game = createGame(context, gameRoot);

  // 名場面の録画（設定で有効にしたときだけ。プレイ中だけ回す）
  let highlightScene: string | null = null;
  let highlightScore = 0;
  bus.on('tour:scene', ({ scene }) => {
    highlightScene = scene?.name ?? null;
  });
  bus.on('score:change', ({ score }) => {
    highlightScore = score;
  });
  const syncRecorder = () => {
    const want = context.settings.recordHighlights && canRecordHighlights();
    if (!want) {
      recorder?.stop();
      recorder = null;
      return;
    }
    if (!recorder && game.canvas) {
      // 縦長（9:16）に合成して、効果音・BGMも入れて録る
      recorder = new HighlightRecorder(game.canvas, {
        info: () => ({ regionName: stage.regionName, sceneName: highlightScene, score: highlightScore }),
        onFrame: (draw) => {
          game.events.on('postrender', draw);
          return () => game.events.off('postrender', draw);
        },
        audio: { open: () => audio.openRecordingStream(), close: () => audio.closeRecordingStream() },
      });
    }
    if (!recorder) return;
    if (state.is(GameState.PLAYING)) recorder.start();
    // ゲームオーバー直後は、合体の瞬間の録画が仕上がるのを少し待ってから止める
    else if (state.is(GameState.GAME_OVER)) window.setTimeout(() => {
      if (!state.is(GameState.PLAYING)) recorder?.stop();
    }, 3500);
    else recorder.stop();
  };
  bus.on('settings:change', syncRecorder);
  state.onChange((to, from) => {
    // 新しいプレイが始まったら前の名場面は捨てる（コンティニューは同じプレイ）
    if (to === GameState.PLAYING && from !== GameState.PAUSED && from !== GameState.GAME_OVER) {
      recorder?.resetClip();
    }
    syncRecorder();
  });
  bus.on('command:restart', () => recorder?.resetClip());
  bus.on('merge:big', ({ level }) => recorder?.capture(level, context.itemByLevel.get(level)?.name));

  // 名場面の静止画（いつも撮る。最高Lv・最大コンボ・最後の盤面の3枚だけ。結果画面の共有カードに載せる）
  moments = new MomentCapture(
    () => game.canvas,
    (draw) => {
      game.events.on('postrender', draw);
      return () => game.events.off('postrender', draw);
    },
  );
  state.onChange((to, from) => {
    if (to === GameState.PLAYING && from !== GameState.PAUSED && from !== GameState.GAME_OVER) {
      moments?.reset();
    }
  });
  bus.on('command:restart', () => moments?.reset());
  bus.on('run:maxLevel', ({ level }) => {
    if (level > 0) moments?.noteLevel(level, context.itemByLevel.get(level)?.name);
  });
  bus.on('combo:change', ({ combo }) => moments?.noteCombo(combo));
  bus.on('run:ending', () => moments?.noteEnding());

  // デバッグ用（ブラウザコンソールから状態を確認できる）
  (window as unknown as { __fukuokaMeshiMerge?: unknown }).__fukuokaMeshiMerge = {
    context,
    game,
    version: '0.1.0',
  };
}

/**
 * 設定に「今日の県のお知らせ」の切り替えを足す（iOS アプリだけ）。
 * 設定画面は index.html に書いてあるが、アプリだけの項目なのでここで足す。
 */
function mountDailyNoticeToggle(prompts: AppPrompts): void {
  const body = document.querySelector('[data-ui="settings-modal"] .modal__body');
  if (!body) return;
  const label = document.createElement('label');
  label.className = 'field field--switch';
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.dataset.ui = 'daily-notice-toggle';
  input.checked = prompts.noticeEnabled;
  const text = document.createElement('span');
  text.className = 'field__label';
  text.textContent = '今日の県の通知（毎朝8時ごろ）';
  const note = document.createElement('span');
  note.className = 'field__note';
  note.hidden = true;
  label.append(input, text, note);
  input.addEventListener('change', async () => {
    input.disabled = true;
    const want = input.checked;
    const on = await prompts.setNoticeEnabled(want).catch(() => false);
    input.checked = on;
    input.disabled = false;
    note.hidden = !(want && !on);
    note.textContent = '通知が許可されていません。iPhone の「設定」→「ご当地めしマージ」→「通知」で許可できます。';
  });
  body.append(label);
}

/**
 * 数秒で消える小さな知らせ。
 *
 * 画面の上に出すとハイスコアと上のボタンを4秒ほど覆っていたので、盤面の下のほう
 * （遊び始めはまだ空の器の中）に出す。押しても下の盤面に指が通る（pointer-events: none）。
 * 豆知識の案内などと同時に出ないよう、お知らせの列に並べる。
 */
/** すぐに出すお知らせ（ボタンを押した結果など。順番待ちの列に並べない）。 */
function showNow(message: string): void {
  const toast = document.createElement('div');
  toast.className = 'toast toast--alert';
  toast.setAttribute('role', 'alert');
  document.body.append(toast);
  window.requestAnimationFrame(() => {
    toast.textContent = message;
  });
  window.setTimeout(() => toast.remove(), 4200);
}

function showToast(message: string, host: HTMLElement | null = null): void {
  // 配信モードのあいだはお知らせを出さない
  if (isStreamerMode()) return;
  const toast = document.createElement('div');
  toast.className = host ? 'toast toast--board' : 'toast';
  toast.setAttribute('role', 'status');
  startupNotices.push({
    durationMs: 4200,
    show: () => {
      (host ?? document.body).append(toast);
      // 読み上げが確実に拾うよう、入れ物を置いてから文字を入れる
      window.requestAnimationFrame(() => {
        toast.textContent = message;
      });
    },
    hide: () => toast.remove(),
  });
}

// アプリの中では絵も音も端末に入っているので、サービスワーカーは要らない（iOS の WKWebView では使えない）
if (!isNativeApp()) registerServiceWorker();
if (isNativeApp()) openSitePagesInBrowser();

/**
 * アプリの中で「プライバシーポリシー」「サポート」などの説明ページ（/xxx.html）を押したとき、
 * アプリの画面の中ではなく Safari で Web版のページを開く（2026-10-20）。
 * アプリの中は capacitor://localhost なので、新しいタブで開こうとすると何も起きないことがあり、
 * 同じ画面で開くと戻るボタンが無い。
 */
function openSitePagesInBrowser(): void {
  document.addEventListener(
    'click',
    (event) => {
      const a = (event.target as Element | null)?.closest?.('a');
      const href = a?.getAttribute('href') ?? '';
      if (!/^\/[\w-]+\.html(?:[?#].*)?$/.test(href)) return;
      event.preventDefault();
      window.open(`${PUBLIC_ORIGIN}${href}`, '_blank', 'noopener');
    },
    true,
  );
}
preventAppZoom();

/**
 * アプリの中だけ、ピンチとダブルタップの拡大を止める（スクロールはできるまま）。
 * Web 版は止めない（文字を大きくして読みたい人がいるので、ブラウザの拡大は残す）。
 */
function preventAppZoom(): void {
  if (!isNativeApp()) return;
  document.documentElement.dataset.app = 'native';
  // iOS の WKWebView はピンチを gesturestart で知らせる。ここで止めれば拡大しない
  const stop = (event: Event) => event.preventDefault();
  document.addEventListener('gesturestart', stop, { passive: false });
  document.addEventListener('gesturechange', stop, { passive: false });
}

void bootstrap().catch((error) => {
  console.error('[main] 起動に失敗しました', error);
});
